import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { getMedicalStatistics, type MedicalStatistics } from '@/api/medicalStatistics.api';
import {
  INHERITANCE_LABELS,
  ASCERTAINMENT_LABELS,
  TRAIT_TYPE_BY_KEY,
} from '@/components/PersonPanel/tabs/medical/medicalLabels';

// ─── Hilfskomponenten (Stil wie StatsPage) ──────────────────────────────────

function Card({ title, children, hint }: { title: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
      <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {hint && <p className="mb-3 text-xs text-slate-400">{hint}</p>}
      {!hint && <div className="mb-3" />}
      {children}
    </div>
  );
}

function Bar({ label, value, max, color = 'bg-blue-500' }: { label: string; value: number; max: number; color?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-28 shrink-0 truncate text-slate-600 sm:w-36" title={label}>{label}</span>
      <div className="h-4 flex-1 rounded-full bg-slate-100">
        <div className={`h-4 rounded-full ${color}`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
      <span className="w-8 text-right font-medium text-slate-700">{value}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-slate-800">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <p className="text-sm text-slate-400">{text}</p>;
}

// ─── Sektionen ────────────────────────────────────────────────────────────

function CoverageSection({ s }: { s: MedicalStatistics }) {
  const c = s.coverage;
  return (
    <Card title="Übersicht & Datenerfassung">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Personen" value={c.totalPersons} />
        <Stat label="Verstorben" value={c.deceased} />
        <Stat label="mit Diagnose" value={c.withConditions} />
        <Stat label="mit Merkmal" value={c.withTraits} />
        <Stat label="Todesursache erfasst" value={c.deceasedWithCause} />
      </div>
    </Card>
  );
}

function AscertainmentSection({ s }: { s: MedicalStatistics }) {
  const max = Math.max(1, ...s.ascertainment.map((a) => a.count));
  const color: Record<string, string> = {
    assessed_positive: 'bg-slate-800',
    assessed_negative: 'bg-slate-400',
    not_assessed: 'bg-amber-400',
  };
  return (
    <Card title="Erfassungsgrad der Diagnosen" hint='Unterscheidet "nicht untersucht" von "kein Befund" – wichtig gegen Erfassungsbias.'>
      {s.ascertainment.length === 0 ? (
        <EmptyHint text="Keine Diagnosen erfasst." />
      ) : (
        <div className="space-y-1.5">
          {s.ascertainment.map((a) => (
            <Bar key={a.level} label={ASCERTAINMENT_LABELS[a.level] ?? a.level} value={a.count} max={max} color={color[a.level] ?? 'bg-blue-500'} />
          ))}
        </div>
      )}
    </Card>
  );
}

function CausesSection({ s }: { s: MedicalStatistics }) {
  const max = Math.max(1, ...s.topCausesOfDeath.map((c) => c.count));
  return (
    <Card title="Häufigste Todesursachen">
      {s.topCausesOfDeath.length === 0 ? (
        <EmptyHint text="Keine Todesursachen erfasst." />
      ) : (
        <div className="space-y-1.5">
          {s.topCausesOfDeath.map((c) => (
            <Bar key={c.label} label={c.label} value={c.count} max={max} color="bg-red-400" />
          ))}
        </div>
      )}
    </Card>
  );
}

function CausesByDecadeSection({ s }: { s: MedicalStatistics }) {
  const max = Math.max(1, ...s.causesByDecade.map((d) => d.count));
  return (
    <Card title="Todesursachen je Jahrzehnt">
      {s.causesByDecade.length === 0 ? (
        <EmptyHint text="Keine datierten Todesfälle." />
      ) : (
        <div className="space-y-1.5">
          {s.causesByDecade.map((d) => (
            <Bar key={d.decade} label={d.decade} value={d.count} max={max} color="bg-slate-500" />
          ))}
        </div>
      )}
    </Card>
  );
}

function InheritanceSection({ s }: { s: MedicalStatistics }) {
  if (s.conditionsByInheritance === null) {
    return (
      <Card title="Vererbungsmuster">
        <p className="rounded bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700">
          🔒 Erfordert die Berechtigung „Erbkrankheiten &amp; Risiko" (medicalSensitive).
        </p>
      </Card>
    );
  }
  const max = Math.max(1, ...s.conditionsByInheritance.map((p) => p.count));
  return (
    <Card title="Erbkrankheiten nach Vererbungsmuster">
      {s.conditionsByInheritance.length === 0 ? (
        <EmptyHint text="Keine Erbkrankheiten erfasst." />
      ) : (
        <div className="space-y-1.5">
          {s.conditionsByInheritance.map((p) => (
            <Bar key={p.pattern} label={INHERITANCE_LABELS[p.pattern] ?? p.pattern} value={p.count} max={max} color="bg-violet-500" />
          ))}
        </div>
      )}
    </Card>
  );
}

function TraitSection({ s }: { s: MedicalStatistics }) {
  if (s.traitDistributions.length === 0) {
    return (
      <Card title="Merkmals-Verteilungen">
        <EmptyHint text="Keine Merkmale erfasst." />
      </Card>
    );
  }
  return (
    <>
      {s.traitDistributions.map((d) => {
        const def = TRAIT_TYPE_BY_KEY[d.traitType];
        const label = def?.label ?? d.traitType;
        const max = Math.max(1, ...d.values.map((v) => v.count));
        const valueLabel = (code: string) => def?.options?.find((o) => o.value === code)?.label ?? code;
        return (
          <Card key={d.traitType} title={label}>
            {d.numeric && (
              <div className="mb-3 grid grid-cols-4 gap-2">
                <Stat label="Anzahl" value={d.numeric.count} />
                <Stat label="Min" value={`${d.numeric.min}${def?.unit ? ' ' + def.unit : ''}`} />
                <Stat label="Ø" value={`${d.numeric.avg}${def?.unit ? ' ' + def.unit : ''}`} />
                <Stat label="Max" value={`${d.numeric.max}${def?.unit ? ' ' + def.unit : ''}`} />
              </div>
            )}
            {d.values.length > 0 && (
              <div className="space-y-1.5">
                {d.values.map((v) => (
                  <Bar key={v.value} label={valueLabel(v.value)} value={v.count} max={max} color="bg-teal-500" />
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function MedicalStatsPage() {
  const { data: stats, isLoading, isError } = useQuery<MedicalStatistics>({
    queryKey: ['medical', 'statistics'],
    queryFn: getMedicalStatistics,
  });

  return (
    <AppLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-5xl">
          <h1 className="mb-1 text-2xl font-bold text-slate-800">Medizin-Statistik</h1>
          <p className="mb-6 text-xs text-slate-400">
            Aggregierte Auswertung (nur Zähler, keine Namen). Keine medizinische Diagnose; ersetzt keine humangenetische Beratung.
          </p>

          {isLoading && (
            <div className="flex justify-center py-20">
              <LoadingSpinner size="lg" message="Statistik wird berechnet..." />
            </div>
          )}
          {isError && <p className="text-center text-red-500">Fehler beim Laden der Statistik.</p>}

          {stats && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><CoverageSection s={stats} /></div>
              <CausesSection s={stats} />
              <CausesByDecadeSection s={stats} />
              <AscertainmentSection s={stats} />
              <InheritanceSection s={stats} />
              <TraitSection s={stats} />
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default MedicalStatsPage;
